import { datadogLogs } from "@datadog/browser-logs";
import { extractId } from "@repo/shopify/src/utils";
import { transformPhoneWithDash } from "@repo/utils/src/number";
import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import useUser from "@hooks/reducerHooks/useUser";
import usePayment from "@hooks/usePayment";

import { ORDER_TAG, calculateShippingFee } from "@utils/archive-sale.ts";

const CheckoutContext = createContext();
export default CheckoutContext;

export function CheckoutProvider({ children }) {
  const { customer } = useUser();

  /** BLOCKED CUSTOMER */
  /** 차단된 고객 여부 */
  const [isBlockedCustomer, setIsBlockedCustomer] = useState(false);

  /** SHIPPING ADDRESS */
  /** 선택된 배송지 id */
  const [selectedAddressId, setSelectedAddressId] = useState();
  /** 고객 배송지 목록 */
  const [addresses, setAddresses] = useState([]);
  /** 선택된 배송지 */
  const selectedAddress = useMemo(() => {
    return addresses.length
      ? addresses.find(
          (address) => extractId(address.id) === extractId(selectedAddressId ?? customer.defaultAddress?.id),
        )
      : null;
  }, [customer.defaultAddress?.id, addresses, selectedAddressId]);

  // /** MILEAGE */
  // const mileage = 10000;
  // const [appliedMileage, setAppliedMileage] = useState(0);

  /** DISCOUNT CODE */
  const [discountCode, setDiscountCode] = useState("");

  /** ORDER SUMMARY */
  const [lineItems, setLineItems] = useState([]);

  /** PAYMENT */
  const [payment, setPayment] = useState();

  const { t } = useTranslation();

  /** 주문생성 API 호출 Parameter 데이터 */
  const paymentParameter = useMemo(() => {
    if (!customer.id || !payment || !lineItems || !selectedAddress) return;

    return {
      orderName:
        lineItems.length > 1
          ? `${lineItems[0]?.name} ${t("account.order.except")} ${lineItems.length - 1}${t("account.order.case")}`
          : lineItems[0]?.name,
      orderData: {
        customer: { id: extractId(customer.id) },
        line_items: lineItems.map((item) => ({
          variant_id: item.id,
          quantity: item.quantity,
          applied_discounts: item.discounts,
        })),
        shipping_address: {
          first_name: selectedAddress.firstName,
          last_name: selectedAddress.lastName,
          phone: transformPhoneWithDash(selectedAddress.phone),
          zip: selectedAddress.zip,
          address1: selectedAddress.address1,
          address2: selectedAddress.address2,
          country: selectedAddress.country ?? "South Korea",
          province: selectedAddress.province ?? "",
          city: selectedAddress.city ?? "-",
          company: "",
        },
        ...(discountCode ? { discount_codes: [{ code: discountCode, amount: 0 }] } : {}),
        shipping_lines: [{ title: t("account.shipping.cj"), price: payment.shippingFee ?? 0 }],
        tags: [ORDER_TAG],
      },
    };
  }, [customer, payment, lineItems, selectedAddress, discountCode]);

  const { isLoading, requestPayment, isProgressing, setIsProgressing, postPayment, paymentMethod } = usePayment({
    payment,
    paymentParameter,
    customerId: extractId(customer.id),
  });

  const extractCart = useCallback(
    async (data) => {
      setIsBlockedCustomer(data?.buyerIdentity?.customer?.tags.includes("Blocked") || false);

      if (!data) return;

      // lineItems
      const lines = data?.lines.edges
        ?.filter(({ node }) => node?.merchandise?.availableForSale)
        .map(({ node }) => {
          const {
            merchandise,
            quantity,
            cost: { compareAtAmountPerQuantity, amountPerQuantity, totalAmount },
            discountAllocations,
          } = node;

          return {
            id: extractId(merchandise.id),
            name: merchandise?.product?.title,
            quantity,
            options: merchandise?.selectedOptions
              ?.filter((option) => option.name === "Size")
              .map((option) => `${option.name} ${option.value}`),
            price: parseInt(Math.min(amountPerQuantity?.amount * quantity, totalAmount?.amount)),
            compareAtPrice: parseInt(compareAtAmountPerQuantity?.amount * quantity),
            discounts: discountAllocations.map((discount) => ({
              description: discount.code ?? discount.title,
              amount: parseInt(discount.discountedAmount.amount),
            })),
          };
        });

      setLineItems(lines);

      // discountCode;
      const [extractedDiscountCode] = data.discountCodes.filter((discountCode) => discountCode.code !== "");
      if (!extractedDiscountCode) setDiscountCode("");
      else {
        extractedDiscountCode.applicable && setDiscountCode(extractedDiscountCode.code);
      }

      // payment
      const discountDescriptions = lines.flatMap((item) => {
        return item.discounts.map((discount) => discount.description);
      });

      const totalDiscount = lines.reduce((acc, lines) => {
        lines?.discounts?.forEach((discount) => {
          acc += discount.amount;
        });
        return acc;
      }, 0);

      const totalAmount = lines.reduce((sum, line) => (sum += line.price), 0);

      setPayment({
        subtotalAmount: lines.reduce((sum, line) => (sum += line.compareAtPrice), 0),
        shippingFee: calculateShippingFee(totalAmount),
        totalAmount: totalAmount,
        totalDiscount,
        discountDescriptions,
      });
    },
    [setLineItems, setPayment, setDiscountCode],
  );

  // Context API에서 다루는 데이터
  const data = useMemo(
    () => ({
      isBlockedCustomer,
      customerId: extractId(customer.id),
      selectedAddress,
      selectedAddressId,
      setSelectedAddressId,
      addresses,
      setAddresses,
      discountCode,
      setDiscountCode,
      lineItems,
      setLineItems,
      payment,
      setPayment,
      paymentParameter,
      extractCart,

      /** usePayment hook return */
      isLoading,
      requestPayment,
      isProgressing,
      setIsProgressing,
      postPayment,
      paymentMethod,
    }),
    [
      customer,
      selectedAddress,
      selectedAddressId,
      addresses,
      discountCode,
      lineItems,
      payment,
      paymentParameter,
      isLoading,
      requestPayment,
      isProgressing,
      setIsProgressing,
      postPayment,
      paymentMethod,
      extractCart,
      isBlockedCustomer,
    ],
  );

  return <CheckoutContext.Provider value={data}>{children}</CheckoutContext.Provider>;
}

export const useCheckoutContext = () => {
  const context = useContext(CheckoutContext);
  if (!context) {
    datadogLogs.logger.error("[CHECKOUT CONTEXT] Something went wrong", context);
    throw new Error(`[CHECKOUT CONTEXT] Something went wrong : ${JSON.stringify(context)}`);
  }
  return context;
};

export const withCheckoutProvider = (WrappedComponent) => {
  function Component(props) {
    return (
      <CheckoutProvider>
        <WrappedComponent {...props} />
      </CheckoutProvider>
    );
  }

  Component.displayName = `useCheckoutContext(${WrappedComponent.displayName || WrappedComponent.name || "Component"})`;

  return Component;
};
